import { isTypo, nameSkeleton, nameTokens, scorePair, type PersonLite } from './duplicates.ts';
import { normalizeForMatch } from './routing.ts';

/**
 * "Did you mean …?" checks while creating records, so typos and small variations do not create duplicates
 * ("Saffron Events" vs "Saffron Events L.L.C", "Safron Events", "سافرون للفعاليات" vs "Saffron Events").
 */

/** Legal forms and filler words that do not distinguish one company from another. */
const COMPANY_NOISE = new Set([
  'llc',
  'l.l.c',
  'lc',
  'fze',
  'fzco',
  'fzc',
  'fz',
  'dmcc',
  'co',
  'company',
  'corp',
  'corporation',
  'inc',
  'ltd',
  'limited',
  'plc',
  'pvt',
  'private',
  'est',
  'establishment',
  'trading',
  'group',
  'holding',
  'holdings',
  'international',
  'intl',
  'the',
  'and',
  'of',
  'llp',
  'wll',
  'w.l.l',
  'spc',
  'saoc',
  'saog',
  'psc',
  'pjsc',
  'bsc',
  'شركه',
  'مؤسسه',
  'ذمم',
  'ذ.م.م',
  'المحدوده',
  'محدوده',
  'للتجاره',
  'التجاريه',
  'مجموعه',
  'القابضه',
  'و',
]);

/** Comparable form of a company name: case, punctuation, Arabic spelling variants and legal suffixes removed. */
export function companyKey(name: string): string {
  return normalizeForMatch(name)
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}\s.]/gu, ' ')
    .split(/\s+/)
    .map((t) => t.replace(/\.+$/, ''))
    .filter((t) => t && !COMPANY_NOISE.has(t) && !COMPANY_NOISE.has(t.replace(/\./g, '')))
    .join(' ');
}

export type SimilarReason =
  | 'same_name'
  | 'same_words'
  | 'similar_name'
  | 'contains'
  | 'same_name_across_scripts'
  | 'same_phone'
  | 'same_email';

export interface Similar {
  id: string;
  score: number;
  reasons: SimilarReason[];
}

export function similarCompanies(
  name: string,
  companies: { id: string; name: string; nameAr?: string | null }[],
  threshold = 0.7,
): Similar[] {
  const q = companyKey(name);
  if (q.length < 2) return [];
  const qSorted = q.split(' ').sort().join(' ');
  const qSkel = nameSkeleton(name);
  const out: Similar[] = [];
  for (const c of companies) {
    let best: Similar | null = null;
    for (const n of [c.name, c.nameAr].filter(Boolean) as string[]) {
      const k = companyKey(n);
      if (!k) continue;
      let score = 0;
      const reasons: SimilarReason[] = [];
      if (k === q) {
        score = 1;
        reasons.push('same_name');
      } else if (k.split(' ').sort().join(' ') === qSorted) {
        score = 0.95;
        reasons.push('same_words');
      } else {
        if (isTypo(k, q)) {
          score = 0.9;
          reasons.push('similar_name');
        }
        const [short, long] = k.length < q.length ? [k, q] : [q, k];
        if (short.length >= 5 && (long.startsWith(`${short} `) || long.endsWith(` ${short}`))) {
          score = Math.max(score, 0.8);
          reasons.push('contains');
        }
        const skel = nameSkeleton(n);
        if (skel.length >= 4 && skel === qSkel) {
          score = Math.max(score, 0.85);
          reasons.push('same_name_across_scripts');
        }
      }
      if (score >= threshold && (!best || score > best.score))
        best = { id: c.id, score: Math.round(score * 100) / 100, reasons };
    }
    if (best) out.push(best);
  }
  return out.sort((a, b) => b.score - a.score);
}

/** People who may already exist: the same phone or email is certain; similar names (any script) are likely. */
export function similarPeople(
  input: { name: string; phones?: string[]; emails?: string[] },
  people: PersonLite[],
  // Lower than the review queue's bar: this only prompts "did you mean…?" before creating.
  threshold = 0.35,
): Similar[] {
  const phones = new Set(
    (input.phones ?? []).map((p) => p.replace(/\D/g, '').slice(-9)).filter((p) => p.length >= 8),
  );
  const emails = new Set((input.emails ?? []).map((e) => e.trim().toLowerCase()).filter(Boolean));
  const probe: PersonLite = {
    id: '\u0000probe',
    name: input.name,
    nameArabic: null,
    emails: [...emails],
    phones: input.phones ?? [],
    accountIds: [],
  };
  const hasName = nameTokens(input.name).length > 0;
  const out: Similar[] = [];
  for (const p of people) {
    const reasons: SimilarReason[] = [];
    let score = 0;
    if (p.phones.some((x) => phones.has(x.replace(/\D/g, '').slice(-9)))) {
      reasons.push('same_phone');
      score = 1;
    }
    if (p.emails.some((x) => emails.has(x.toLowerCase()))) {
      reasons.push('same_email');
      score = 1;
    }
    if (hasName) {
      const pair = scorePair(probe, p);
      for (const r of pair.reasons)
        if (
          r === 'same_name' ||
          r === 'same_name_reordered' ||
          r === 'similar_name' ||
          r === 'same_name_across_scripts'
        )
          reasons.push(r === 'same_name_reordered' ? 'same_words' : r);
      score = Math.max(score, pair.score);
    }
    if (score >= threshold) out.push({ id: p.id, score: Math.round(score * 100) / 100, reasons });
  }
  return out.sort((a, b) => b.score - a.score);
}
